import { CommandId, MessageId, ProjectId, ThreadId } from "@cmmd-forge/contracts";
import { type CxOptions, cx } from "class-variance-authority";
import { extendTailwindMerge } from "tailwind-merge";
import * as Random from "effect/Random";
import * as Effect from "effect/Effect";
import { DraftId } from "../draftId";

/**
 * `text-no-zoom` is a custom font-size token (16px, declared in `index.css`).
 * tailwind-merge classifies an unrecognised `text-*` value as a *colour*, so out
 * of the box `cn("text-no-zoom", "text-foreground")` returned just
 * `"text-foreground"` — silently dropping the field sizing that stops iOS Safari
 * zooming on focus, while the class was still plainly written in the source.
 * Registering it in the font-size group makes the conflict resolve correctly:
 * it survives a colour, and still loses to a later real font-size.
 *
 * `2xs` / `3xs` are the upstream union's dense-UI sizes (index.css), registered
 * under `theme.text` the way upstream's own `lib/utils.ts` does, so a vendored
 * `text-2xs` next to `text-muted-foreground` keeps both classes.
 */
const twMerge = extendTailwindMerge({
  extend: {
    theme: { text: ["2xs", "3xs"] },
    classGroups: { "font-size": [{ text: ["no-zoom"] }] },
  },
});

export function cn(...inputs: CxOptions) {
  return twMerge(cx(inputs));
}

export function isMacPlatform(platform: string): boolean {
  return /mac|iphone|ipad|ipod/i.test(platform);
}

export function isWindowsPlatform(platform: string): boolean {
  return /^win(dows)?/i.test(platform);
}

export function isLinuxPlatform(platform: string): boolean {
  return /linux/i.test(platform);
}

/**
 * What to call the machine Forge is running on, in UI copy. Forge ships on
 * macOS, Windows and Linux, so "this Mac" is wrong for most of them; every
 * label that names the local device composes this instead of hardcoding one
 * platform. Falls back to the platform-neutral "this device" when the platform
 * string is unrecognised, which is always true rather than confidently wrong.
 */
export function localDeviceNoun(
  platform: string = typeof navigator === "undefined" ? "" : navigator.platform,
): string {
  if (isMacPlatform(platform)) return "this Mac";
  if (isWindowsPlatform(platform)) return "this PC";
  if (isLinuxPlatform(platform)) return "this Linux machine";
  return "this device";
}

/**
 * Title-case form of {@link localDeviceNoun}, for labels and buttons
 * ("This Mac", "This PC") rather than sentences ("Runs on this Mac").
 */
export function localDeviceLabel(
  platform: string = typeof navigator === "undefined" ? "" : navigator.platform,
): string {
  const noun = localDeviceNoun(platform);
  return `${noun.charAt(0).toUpperCase()}${noun.slice(1)}`;
}

export function randomUUID(): string {
  if (typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return Effect.runSync(Random.nextUUIDv4);
}

export const newCommandId = (): CommandId => CommandId.make(randomUUID());

export const newProjectId = (): ProjectId => ProjectId.make(randomUUID());

export const newThreadId = (): ThreadId => ThreadId.make(randomUUID());

export const newDraftId = (): DraftId => DraftId.make(randomUUID());

export const newMessageId = (): MessageId => MessageId.make(randomUUID());
